import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Grid, Typography } from "@mui/material";
import Button from "components/button/Button";
import { useParams } from "react-router-dom";

import { BeatLoader } from "react-spinners";
import { toastr } from "react-redux-toastr";
import { useSearch } from "hook";
import { ReactComponent as NextArrow } from "assets/icons/NextArrow.svg";
import { ReactComponent as NoteIcon } from "assets/icons/NoteIcon.svg";
import { MultiSelect } from "components/MultiSelect/MultiSelect";
import { InputField } from "components/InputField/InputField";
import MyModal from "components/MyModal";
import { Card } from "./Card";
import {
  CardContent,
  MemberWrapper,
  Title,
  AccountId,
  AccountName,
  DescriptionWrapper,
  Desc,
  ExplainActions,
  Note,
  MergeInput,
  ContentWrapper,
  Wrapper,
} from "./Theme.js";

import FinanceService from "Lib/API/Finance/index";

export const MergeFamilyModal = ({
  isOpen,
  handleClose,
  closeSuccessModal,
  users,
}) => {
  const { id } = useParams();
  const [accounts, setAccounts] = useState([]);
  const { getAccessTokenSilently } = useAuth0();
  const { searchAction } = useSearch({ action: setSearch });
  const [accountLoading, setAccountLoading] = useState(false);
  const [search, setSearch] = useState({ value: "", type: "" });

  const formik = useFormik({
    initialValues: {
      account: {
        accounts: [],
      },
      selectedAccount: null,
      confirmText: "",
      error: false,
    },
    validationSchema: Yup.object({
      selectedAccount: Yup.object()
        .typeError("Please select an account")
        .required("Please select an account"),
      confirmText: Yup.string()
        .required("Confirmation text is required")
        .test("is-merge", 'Must type "Merge"', (value) =>
          value?.toLowerCase().includes("merge")
        ),
    }),
    onSubmit: async (values) => {
      try {
        const token = await getAccessTokenSilently();
        const payload = {
          from_id: values?.selectedAccount?.id,
          to_id: users?._id,
        };

        await FinanceService.mergeContact(token, payload);
        toastr.success("Success", "Contact merged successfully");
        closeSuccessModal();
      } catch (err) {
        toastr.error("Error", err?.data?.message || "Merge failed");
      }
    },
  });

  const { setFieldValue, values, handleSubmit, isSubmitting } = formik;

  const getContactById = async () => {
    const token = await getAccessTokenSilently();
    setAccountLoading(true);
    try {
      const res = await FinanceService.getAccountByID(token, id);
      if (res?.contacts) {
        const newArray = res?.contacts
          ?.filter((el) => el._id !== users?._id)
          ?.map((item) => ({
            ...item,
            title: item?.full_name,
            id: item?._id,
          }));
        setAccounts(newArray);
      }
    } catch (err) {
      toastr.error("Error", err?.data?.message);
      setAccounts([]);
    } finally {
      setAccountLoading(false);
    }
  };

  useEffect(() => {
    getContactById();
  }, []);

  const actions = [
    `All the yahrzeits on contact ${
      values.selectedAccount?.title || "-"
    } will be transferred to contact ${users?.full_name || "-"}`,

    `All the registrations on contact ${
      values.selectedAccount?.title || "-"
    } will be transferred to contact ${users?.full_name || "-"}`,

    `All fields on contact ${
      values.selectedAccount?.title || "-"
    } will be transferred to contact ${users?.full_name || "-"}`,
  ];

  console.log(formik.values);

  const handleFormikChange = (items) => setFieldValue("selectedAccount", items);

  return (
    <MyModal open={isOpen} onClose={handleClose} className="merge-modal">
      <ContentWrapper>
        <Typography variant="h2" className="merge-modal__title">
          Merge contacts
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={4} sx={{ marginTop: "10px" }}>
            <Grid item xs={12}>
              <Grid container justifyContent="center" spacing={2}>
                <Grid
                  item
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Card style={{ width: "368px", height: "200px" }}>
                    <CardContent>
                      <MemberWrapper>
                        {values.selectedAccount?.account_type && (
                          <Title className="text-capitalize">
                            {values.selectedAccount?.account_type}
                          </Title>
                        )}
                      </MemberWrapper>
                      <div style={{ width: "100%", marginBottom: "24px" }}>
                        <MultiSelect
                          placeholder="Select"
                          label={"Contacts Name"}
                          isOptinal={false}
                          isCheckBoxOption={false}
                          options={accounts}
                          loading={accountLoading}
                          setOptions={setAccounts}
                          variableForSave={"title"}
                          formik={formik}
                          handleFormikChange={handleFormikChange}
                          isAccount={true}
                          searchAction={searchAction}
                        />
                        {formik.touched.selectedAccount &&
                          formik.errors.selectedAccount && (
                            <div style={{ color: "red" }}>
                              {formik.errors.selectedAccount}
                            </div>
                          )}
                      </div>

                      {formik.values.selectedAccount?.member_id && (
                        <AccountId>
                          #{formik.values.selectedAccount.member_id}
                        </AccountId>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid
                  item
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <NextArrow />
                </Grid>

                <Grid item>
                  <Card style={{ width: "368px", height: "200px" }}>
                    <CardContent>
                      <MemberWrapper>
                        <Title className="text-capitalize">
                          {users?.account_type}
                        </Title>
                      </MemberWrapper>
                      <AccountName>{users?.full_name}</AccountName>
                      <AccountId>#{users?.member_id}</AccountId>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <DescriptionWrapper>
                <Desc>
                  <span>{values.selectedAccount?.title || "-"} </span> will be
                  merged into {users?.full_name}
                </Desc>
              </DescriptionWrapper>
              {values.selectedAccount?.title && (
                <ExplainActions>
                  <div style={{ width: "100%" }}>
                    {actions?.map((item, index) => (
                      <span>
                        {index + 1}. {item}
                        <br />
                      </span>
                    ))}
                  </div>
                  <Note>
                    <NoteIcon />
                    <span>This action can not be undone</span>
                  </Note>
                </ExplainActions>
              )}
              {values.selectedAccount?.title && (
                <MergeInput>
                  <InputField
                    label={
                      <div style={{ fontWeight: "normal" }}>
                        Type <span style={{ fontWeight: "bolder" }}>Merge</span>{" "}
                        to confirm
                      </div>
                    }
                    isOptional={false}
                    type="text"
                    value={formik.values.confirmText}
                    onChange={(e) =>
                      formik.setFieldValue("confirmText", e.target.value)
                    }
                    maxLength="5"
                    onKeyPress={(e) => e.key === "Enter" && e.preventDefault()}
                    error={
                      formik.touched.confirmText && formik.errors.confirmText
                    }
                    flexDirection="column"
                  />
                </MergeInput>
              )}
              {values.selectedAccount?.title && (
                <Wrapper>
                  <Button
                    type="button"
                    onClick={handleClose}
                    variant="primary-outline"
                    size="md"
                    style={{ minWidth: "100px" }}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    style={{ minWidth: "100px" }}
                    disabled={
                      isSubmitting ||
                      values?.confirmText?.toLowerCase() !== "merge"
                    }
                  >
                    {isSubmitting ? (
                      <BeatLoader color="#fff" size={6} margin={1} />
                    ) : (
                      "Merge"
                    )}
                  </Button>
                </Wrapper>
              )}
            </Grid>
          </Grid>
        </form>
      </ContentWrapper>
    </MyModal>
  );
};
